// Imported from the constants module directly (not the @lib/clevertap
// barrel) so this pure helper never pulls in the CleverTap browser wrapper.
import { ClevertapEventProperties } from '@lib/clevertap/sdk/constants';

export type FunnelSufSource = 'GYTR' | 'MLR';
export type FunnelGwTiming = 'pre_gw1' | 'post_gw1';

export type FunnelDimensions = {
  sufSource: FunnelSufSource;
  gwTiming: FunnelGwTiming;
};

/**
 * Derives the spec's funnel dimensions from the Sanity quiz-page templateKey
 * (`gytr-pre-gw1` | `gytr-post-gw1` | `readiness-pre-gw1` |
 * `readiness-post-gw1`). Values are case-sensitive in CleverTap filters, so
 * they must match the spec exactly. Mirrored server-side in
 * ffh-public-api's modules/funnels/lib/funnel-dimensions.ts.
 */
export const getFunnelDimensions = (templateKey: string): FunnelDimensions => ({
  sufSource: templateKey.startsWith('gytr-') ? 'GYTR' : 'MLR',
  gwTiming: templateKey.endsWith('post-gw1') ? 'post_gw1' : 'pre_gw1'
});

/**
 * Builds the funnel-completed result snapshot sent to the backend via
 * `submitFunnelResultSnapshot`. The backend relays these fields to CleverTap
 * verbatim (ffh-public-api's funnel-completed-uploader.ts), so this shape
 * must stay in sync with what it expects. The completion date is
 * deliberately NOT set here — the backend stamps it server-side, since
 * browser clocks can't be trusted for a property CleverTap journeys filter
 * on. The dimensions are applied after resultProps so they always win
 * (the backend also enforces its own server-derived copies).
 */
export const buildFunnelCompletedSnapshot = (
  dimensions: FunnelDimensions,
  resultProps: Record<string, unknown> = {}
): Record<string, unknown> => ({
  ...resultProps,
  [ClevertapEventProperties.sufSource]: dimensions.sufSource,
  [ClevertapEventProperties.gwTiming]: dimensions.gwTiming
});
